/**
 * Water & mash-pH section commands (PLAN §5.4, §5.5).
 *
 * `water.setSourceProfile` is the only one that creates the section; the rest
 * require it and return `invalid-state` otherwise. Materialising a water
 * section from "add 2 g gypsum" would have to invent a source profile, and an
 * invented source profile produces a confidently wrong mash pH — the exact
 * failure mode this feature exists to avoid.
 *
 * Salts and acids are replaced **wholesale** rather than one row at a time.
 * That is the shape the solver emits ("here is the set of additions that hits
 * your target"), the shape the assistant proposes, and it makes the inverse a
 * single prior array instead of a per-row diff.
 */

import type { RecipeDoc } from '../../document/recipe';
import { blankRecipeWater } from '../../document/water';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { num } from '../format';
import { withRecipe } from '../helpers';
import * as p from '../payloads';

/** `invalid-state` for a water command on a recipe with no water section. */
function noWaterSection() {
  return commandError(
    'invalid-state',
    'This recipe has no water section. Send water.setSourceProfile first.',
  );
}

/** Apply `mutate` to the recipe's water section, or fail if there is none. */
function withWater(
  recipe: RecipeDoc,
  mutate: (water: NonNullable<RecipeDoc['water']>) => NonNullable<RecipeDoc['water']>,
) {
  if (recipe.water === null) return noWaterSection();
  return { ...recipe, water: mutate(recipe.water) };
}

/**
 * Set the source water, creating the section when the recipe has none.
 *
 * Its inverse is `water.clear` when the section did not exist, and a
 * `water.setSourceProfile` carrying the old profile when it did — so undo is
 * exact in both directions.
 */
export const waterSetSourceProfile = defineCommand({
  type: 'water.setSourceProfile',
  payload: p.waterSetSourceProfilePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      water:
        recipe.water === null
          ? blankRecipeWater(command.payload.profile)
          : { ...recipe.water, sourceProfile: command.payload.profile },
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    return prior.water === null
      ? inverseCommand(command, 'water.clear', { recipeId: prior.id }, prior.updatedAt)
      : inverseCommand(
          command,
          'water.setSourceProfile',
          { recipeId: prior.id, profile: prior.water.sourceProfile },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    `Set source water to ${command.payload.profile.name ?? 'a custom report'}`,
});

/** Set the RO/distilled dilution. */
export const waterSetDilution = defineCommand({
  type: 'water.setDilution',
  payload: p.waterSetDilutionPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({
        ...water,
        dilutionFraction: command.payload.dilutionFraction,
        dilutionProfile:
          command.payload.dilutionProfile === undefined
            ? water.dilutionProfile
            : command.payload.dilutionProfile,
      })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(
      command,
      'water.setDilution',
      {
        recipeId: prior.id,
        dilutionFraction: prior.water.dilutionFraction,
        dilutionProfile: prior.water.dilutionProfile,
      },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    `Dilute the brewing water ${num(command.payload.dilutionFraction * 100, 1)} % with RO`,
});

/** Replace the salt additions. */
export const waterSetSaltAdditions = defineCommand({
  type: 'water.setSaltAdditions',
  payload: p.waterSetSaltAdditionsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({
        ...water,
        saltAdditions: [...command.payload.saltAdditions],
      })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(
      command,
      'water.setSaltAdditions',
      { recipeId: prior.id, saltAdditions: [...prior.water.saltAdditions] },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const additions = command.payload.saltAdditions;
    if (additions.length === 0) return 'Remove all salt additions';
    return `Set salts: ${additions.map((s) => `${num(s.grams, 2)} g ${s.salt}`).join(', ')}`;
  },
});

/** Replace the acid additions. */
export const waterSetAcidAdditions = defineCommand({
  type: 'water.setAcidAdditions',
  payload: p.waterSetAcidAdditionsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({
        ...water,
        acidAdditions: [...command.payload.acidAdditions],
      })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(
      command,
      'water.setAcidAdditions',
      { recipeId: prior.id, acidAdditions: [...prior.water.acidAdditions] },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const additions = command.payload.acidAdditions;
    if (additions.length === 0) return 'Remove all acid additions';
    return `Set acids: ${additions
      .map((a) =>
        a.millilitres === null
          ? `${num(a.grams ?? 0, 2)} g ${a.acid}`
          : `${num(a.millilitres, 2)} ml ${a.acid}${
              a.concentrationPercent === null ? '' : ` ${num(a.concentrationPercent, 1)} %`
            }`,
      )
      .join(', ')}`;
  },
});

/**
 * Set or clear the target water profile the achieved water is reported against.
 *
 * Setting one **clears any water rule**, because the section aims at a profile
 * or at a rule and never both (PLAN-WATER-RULES D-WR1, enforced by
 * `recipeWaterSchema`). Clearing the profile leaves the rule alone: `null` here
 * means "no profile", not "no target at all", and a `setTargetProfile(null)`
 * that wiped a rule would delete a target the brewer never touched.
 *
 * Its inverse therefore has two shapes. When the prior section held a rule, the
 * undo is a {@link waterSetTargetRule} carrying that rule — which restores the
 * rule *and* re-nulls the profile in one command, because that is what applying
 * a rule does. Otherwise it is the plain profile restore.
 */
export const waterSetTargetProfile = defineCommand({
  type: 'water.setTargetProfile',
  payload: p.waterSetTargetProfilePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({
        ...water,
        targetProfile: command.payload.profile,
        targetRule: command.payload.profile === null ? water.targetRule : null,
      })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    if (prior.water.targetRule !== null) {
      return inverseCommand(
        command,
        'water.setTargetRule',
        { recipeId: prior.id, rule: prior.water.targetRule },
        prior.updatedAt,
      );
    }
    return inverseCommand(
      command,
      'water.setTargetProfile',
      { recipeId: prior.id, profile: prior.water.targetProfile },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    command.payload.profile === null
      ? 'Clear the target water profile'
      : `Set target water to ${command.payload.profile.name ?? 'a custom profile'}`,
});

/**
 * Aim the water at a rule — a calcium floor and a sulfate-to-chloride balance —
 * instead of at six numbers.
 *
 * The mirror of {@link waterSetTargetProfile} in every respect. Setting a rule
 * clears the target profile; clearing the rule leaves the profile alone; and
 * the inverse restores whichever target the section held before, in one
 * command, so a single undo puts the panel back exactly as it was rather than
 * leaving the recipe aiming at nothing.
 *
 * The two-shaped inverse is what makes that work. One command writes one key,
 * so a `setTargetRule` inverse could only ever restore a rule; when the thing
 * displaced was a *profile*, the honest undo is the other setter, whose own
 * apply re-nulls the rule on the way past.
 */
export const waterSetTargetRule = defineCommand({
  type: 'water.setTargetRule',
  payload: p.waterSetTargetRulePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({
        ...water,
        targetRule: command.payload.rule,
        targetProfile: command.payload.rule === null ? water.targetProfile : null,
      })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    if (prior.water.targetProfile !== null) {
      return inverseCommand(
        command,
        'water.setTargetProfile',
        { recipeId: prior.id, profile: prior.water.targetProfile },
        prior.updatedAt,
      );
    }
    return inverseCommand(
      command,
      'water.setTargetRule',
      { recipeId: prior.id, rule: prior.water.targetRule },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const rule = command.payload.rule;
    if (rule === null) return 'Clear the water rule';
    // `toFixed(1)` rather than `num`, which strips the trailing zero: a balance
    // reads as a balance at "2.0" and as a count at "2".
    return (
      `Aim the water at calcium ${num(rule.minCalciumPpm, 0)} ppm and a ` +
      `${rule.sulfateChlorideRatio.toFixed(1)} sulfate-to-chloride balance`
    );
  },
});

/** Set the target mash pH. */
export const waterSetTargetPh = defineCommand({
  type: 'water.setTargetPh',
  payload: p.waterSetTargetPhPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({ ...water, targetMashPh: command.payload.targetMashPh })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(
      command,
      'water.setTargetPh',
      { recipeId: prior.id, targetMashPh: prior.water.targetMashPh },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => `Set target mash pH to ${num(command.payload.targetMashPh, 2)}`,
});

/**
 * Set the kettle target pH, or clear it back to core's default.
 *
 * No dose comes out of this. The kettle dose is worked out on brew day from a
 * meter reading, because the wort that reaches the boil is not something a
 * recipe can predict the pH of — see `kettlePh.ts` in core. What this stores is
 * the number that reading will be judged against.
 */
export const waterSetKettleTargetPh = defineCommand({
  type: 'water.setKettleTargetPh',
  payload: p.waterSetKettleTargetPhPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) =>
      withWater(recipe, (water) => ({ ...water, kettleTargetPh: command.payload.kettleTargetPh })),
    ),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(
      command,
      'water.setKettleTargetPh',
      { recipeId: prior.id, kettleTargetPh: prior.water.kettleTargetPh },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    command.payload.kettleTargetPh === null
      ? 'Follow the default kettle pH'
      : `Set target kettle pH to ${num(command.payload.kettleTargetPh, 2)}`,
});

/** Drop the whole water section. Its inverse restores it in one command. */
export const waterClear = defineCommand({
  type: 'water.clear',
  payload: p.waterClearPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({ ...recipe, water: null })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined || prior.water === null) return null;
    return inverseCommand(command, 'recipe.restore', { recipe: prior }, prior.updatedAt);
  },
  summarize: () => 'Remove the water & pH section',
});
